import { type AgGridReact } from "@jsq/ds/core";
import {
  type AddressValues,
  type FormFieldConfig,
  type SelectOption,
  type TaxFormType,
} from "../types";

/**
 * All valid TaxFormType values as an array.
 * Used for runtime validation of URL parameters.
 */
export const VALID_TAX_FORM_TYPES: readonly TaxFormType[] = [
  "W8BEN",
  "W8BENE",
  "W8ECI",
  "W8EXP",
  "W8IMY",
  "W9",
  "WITHHOLDING_STATEMENT",
  "CRS_INDIVIDUAL",
  "CRS_ENTITY",
] as const;

/**
 * FATCA form types (W-series tax documents).
 * Used to determine if a tax form is FATCA vs CRS.
 */
export const FATCA_FORM_TYPES: readonly TaxFormType[] = [
  "W8BEN",
  "W8BENE",
  "W8ECI",
  "W8EXP",
  "W8IMY",
  "W9",
  "WITHHOLDING_STATEMENT",
] as const;

/**
 * Determines if a tax form type is a FATCA form (vs CRS).
 */
export const isFatcaFormType = (formType: TaxFormType): boolean => {
  return FATCA_FORM_TYPES.includes(formType);
};

/**
 * Type guard to validate if a string is a valid TaxFormType
 */
export const isValidTaxFormType = (value: string | undefined): value is TaxFormType => {
  return !!value && VALID_TAX_FORM_TYPES.includes(value as TaxFormType);
};

/**
 * Maps TaxFormType to display title for the drawer header
 */
export const TAX_FORM_TITLE_MAP: Record<TaxFormType, string> = {
  W8BEN: "New FATCA details",
  W8BENE: "New FATCA details",
  W8ECI: "New FATCA details",
  W8EXP: "New FATCA details",
  W8IMY: "New FATCA details",
  W9: "New FATCA details",
  WITHHOLDING_STATEMENT: "New FATCA details",
  CRS_INDIVIDUAL: "New CRS details",
  CRS_ENTITY: "New CRS details",
};

/**
 * Maps document sub-type values (from form dropdowns) to TaxFormType for routing.
 * W-Series tax documents.
 */
export const W_SERIES_DOCUMENT_TO_TAX_FORM_TYPE: Record<string, TaxFormType> = {
  "W-9": "W9",
  "W-8BEN": "W8BEN",
  "W-8BEN-E": "W8BENE",
  "W-8ECI": "W8ECI",
  "W-8EXP": "W8EXP",
  "W-8IMY": "W8IMY",
  withholding_statement: "WITHHOLDING_STATEMENT",
};

/**
 * Maps document sub-type values (from form dropdowns) to TaxFormType for routing.
 * Self-certification documents.
 */
export const SELF_CERTIFICATION_DOCUMENT_TO_TAX_FORM_TYPE: Record<string, TaxFormType> = {
  individual_self_certification: "CRS_INDIVIDUAL",
  entity_self_certification: "CRS_ENTITY",
};

export const EMPTY_ADDRESS: AddressValues = {
  country: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  stateOrProvince: "",
  zipCode: "",
};

export const TABLE_GRID_OPTIONS: AgGridReact.AgGridReactProps["gridOptions"] = {
  suppressCellFocus: true,
  domLayout: "autoHeight",
  rowHeight: 86,
  defaultColDef: {
    wrapHeaderText: true,
    autoHeaderHeight: true,
    suppressHeaderMenuButton: true,
    wrapText: true,
    autoHeight: true,
  },
};

type CreateAddressFieldsOptions = {
  helpText?: string;
};

export const createAddressFields = (
  prefix: string,
  options: CreateAddressFieldsOptions = {},
): FormFieldConfig[] => {
  const { helpText } = options;
  return [
    {
      name: `${prefix}.country`,
      type: "country",
      label: "",
      placeholder: "Select a country",
      gridColumn: "full",
    },
    {
      name: `${prefix}.streetLine1`,
      type: "text",
      label: "",
      placeholder: "Street (line 1)",
      helpText,
      gridColumn: "full",
    },
    {
      name: `${prefix}.streetLine2`,
      type: "text",
      label: "",
      placeholder: "Street (line 2)",
      gridColumn: "full",
    },
    {
      name: `${prefix}.city`,
      type: "text",
      label: "",
      placeholder: "City",
      gridColumn: "full",
    },
    {
      name: `${prefix}.stateOrProvince`,
      type: "text",
      label: "",
      placeholder: "State/Province",
      gridColumn: "half",
    },
    {
      name: `${prefix}.zipCode`,
      type: "text",
      label: "",
      placeholder: "Zip/Postal Code",
      gridColumn: "half",
    },
  ];
};

export const CHAPTER_3_STATUS_OPTIONS: SelectOption[] = [
  { value: "corporation", label: "Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "simple_trust", label: "Simple trust" },
  { value: "tax_exempt_organization", label: "Tax-exempt organization" },
  { value: "complex_trust", label: "Complex trust" },
  { value: "grantor_trust", label: "Grantor trust" },
  { value: "estate", label: "Estate" },
  { value: "disregarded_entity", label: "Disregarded entity" },
  { value: "foreign_government_integral_part", label: "Foreign Government – Integral Part" },
  {
    value: "foreign_government_controlled_entity",
    label: "Foreign Government – Controlled Entity",
  },
  { value: "central_bank_of_issue", label: "Central Bank of Issue" },
  { value: "international_organization", label: "International organization" },
  { value: "private_foundation", label: "Private foundation" },
];

export const CHAPTER_4_STATUS_OPTIONS: SelectOption[] = [
  { value: "nonparticipating_ffi", label: "Nonparticipating FFI" },
  { value: "participating_ffi", label: "Participating FFI" },
  { value: "reporting_model_1_ffi", label: "Reporting Model 1 FFI" },
  { value: "reporting_model_2_ffi", label: "Reporting Model 2 FFI" },
  { value: "registered_deemed_compliant_ffi", label: "Registered deemed-compliant FFI" },
  { value: "sponsored_ffi", label: "Sponsored FFI" },
  {
    value: "cert_dc_nonreg_local_bank",
    label: "Certified deemed-compliant nonregistering local bank",
  },
  {
    value: "cert_dc_ffi_low_value_accounts",
    label: "Certified deemed-compliant FFI with only low-value accounts",
  },
  {
    value: "cert_dc_sponsored_closely_held_inv_vehicle",
    label: "Certified deemed-compliant sponsored closely held investment vehicle",
  },
  {
    value: "cert_dc_limited_life_debt_inv_entity",
    label: "Certified deemed-compliant limited life debt investment entity",
  },
  {
    value: "certain_inv_entities_no_financial_accounts",
    label: "Certain investment entities that do not maintain financial accounts",
  },
  { value: "owner_documented_ffi", label: "Owner-documented FFI" },
  { value: "restricted_distributor", label: "Restricted distributor" },
  { value: "nonreporting_iga_ffi", label: "Nonreporting IGA FFI" },
  {
    value: "foreign_govt_us_possession_or_central_bank",
    label:
      "Foreign government, Government of a U.S. possession or Foreign central bank of issue",
  },
  { value: "international_organization", label: "International organization" },
  { value: "exempt_retirement_plans", label: "Exempt retirement plans" },
  {
    value: "entity_wholly_owned_by_exempt_beneficial_owners",
    label: "Entity wholly owned by exempt beneficial owners",
  },
  { value: "territory_financial_institution", label: "Territory financial institution" },
  { value: "excepted_nonfinancial_group_entity", label: "Excepted nonfinancial group entity" },
  {
    value: "excepted_nonfinancial_start_up_company",
    label: "Excepted nonfinancial start-up company",
  },
  {
    value: "excepted_nonfin_entity_liquidation_bankruptcy",
    label: "Excepted nonfinancial entity in liquidation or bankruptcy",
  },
  { value: "section_501_c_organization", label: "501(c) organization" },
  { value: "nonprofit_organization", label: "Nonprofit organization" },
  {
    value: "publicly_traded_nffe_or_affiliate",
    label: "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  },
  { value: "excepted_territory_nffe", label: "Excepted territory NFFE" },
  { value: "active_nffe", label: "Active NFFE" },
  { value: "passive_nffe", label: "Passive NFFE" },
  { value: "excepted_inter_affiliate_ffi", label: "Excepted inter-affiliate FFI" },
  { value: "direct_reporting_nffe", label: "Direct reporting NFFE" },
  { value: "sponsored_direct_reporting_nffe", label: "Sponsored direct reporting NFFE" },
  { value: "account_not_financial_account", label: "Account that is not a financial account" },
];

export const ENTITY_TYPE_OPTIONS: SelectOption[] = [
  { value: "corporation", label: "Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "trust", label: "Trust" },
  { value: "estate", label: "Estate" },
  { value: "llc", label: "Limited Liability Company" },
  { value: "other", label: "Other" },
];

export const LOB_TYPES_OPTIONS: SelectOption[] = [
  { value: "government", label: "Government" },
  { value: "tax_exempt_pension", label: "Tax-exempt pension trust or pension fund" },
  { value: "other_tax_exempt", label: "Other tax-exempt organization" },
  { value: "publicly_traded_corp", label: "Publicly traded corporation" },
  { value: "subsidiary_publicly_traded", label: "Subsidiary of a publicly traded corporation" },
  {
    value: "ownership_base_erosion",
    label: "Company that meets the ownership and base erosion test",
  },
  { value: "derivative_benefits", label: "Company that meets the derivative benefits test" },
  {
    value: "active_trade_business",
    label: "Company with an item of income that meets active trade or business test",
  },
  {
    value: "favorable_discretionary",
    label: "Favorable discretionary determination by the U.S. competent authority received",
  },
  { value: "no_lob_article", label: "No LOB article in treaty" },
  { value: "other", label: "Other" },
];
